/**
 * Entry for the Alpine.js rankings app. The component is composed from the per-concern mixins in
 * alpinejs/rankings; this file only wires Alpine, merges them, and runs their init steps in order.
 */
import Alpine from '/js/alpine/core@3.14.9/alpine.esm.js';
import '../../components/PlayerNameEl.js';
import '../../components/PosRosterSpotEl.js';
import { composeMixins } from './../rankings/RankingsMixins.js';
import coreState from './../rankings/CoreState.js';
import mobileTableChrome from './../rankings/MobileTableChrome.js';
import trendAndPlayerModals from './../rankings/TrendAndPlayerModals.js';
import urlAndScoring from './../rankings/UrlAndScoring.js';
import filtering from './../rankings/Filtering.js';
import columnVisibility from './../rankings/ColumnVisibility.js';
import sorting from './../rankings/Sorting.js';
import guestCta from './../rankings/GuestCta.js';
import cellClasses from './../rankings/CellClasses.js';
import userLeague from './../rankings/UserLeague.js';
import rowLoading from './../rankings/RowLoading.js';

window.Alpine = Alpine;

function registerAlpineComponent(name, component) {
    const register = () => Alpine.data(name, component);
    if (window.Alpine && typeof Alpine.effect === 'function') {
        register();
    } else {
        document.addEventListener('alpine:init', register);
    }
}

/**
 * Alpine.js component for managing rankings visibility
 */
registerAlpineComponent('rankingsApp', () =>
    composeMixins(
        coreState(),
        mobileTableChrome(),
        trendAndPlayerModals(),
        urlAndScoring(),
        filtering(),
        columnVisibility(),
        sorting(),
        guestCta(),
        cellClasses(),
        userLeague(),
        rowLoading(),
        {
            // Each step owns one block of the original init(). The order matters: appData seeding
            // must precede everything that reads basePath or the breakpoint flags, the $watch
            // calls must follow it so they do not fire during init, and everything up to the
            // worker step runs synchronously, as it always has.
            async init() {
                this.initBreakpoints();
                this.initResizeSync();
                this.initCellClassHelpers();
                this.initAppData();
                this.initMobileTableChrome();
                this.initSelectedColumns();

                // The server rendered the initial rows with this slug. A redirected user league
                // selection below overwrites pprScoring/superflex with the league's scoring, so
                // capture the slug now to keep the load-rows requests aligned with the SSR rows.
                const initialPprSuperflexSlug = this.getPprSuperflexSlug();
                this.initRedirectedUserLeague();

                this.initParkedRows();
                this.initColumnVisibilityStyles();
                this.initRosterStatusEffect();
                this.initFiltering();
                this.initSections();
                this.initCurrentVisibleTier();
                this.initModalTriggers();
                this.initDelegatedClicks();
                this.initTableSwapHandler();
                this.initRowLoading(initialPprSuperflexSlug);

                await this.initWorker();

                this.initAfterSwapListener();
            },
        }
    )
);

// Initialize Alpine.js
Alpine.start(() => {
    console.log('App Started');
});
